import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type MapConfigView, type ServiceAreasView, type ServiceAreaPublicationReasonView } from '@vello/api-client';
import { formatDistance, type FormatContext } from '@vello/format';
import { SERVICE_AREA_LIMITS, SERVICE_AREA_PUBLICATION_REASONS, importServiceArea, serviceAreaRings } from '@vello/geofence';
import { AUTO_REFRESH_MS, Badge, Button, CheckRow, EmptyState, Field, FileInput, ListTable, Measure, Notice, PageHeader, Panel, Row, SectionTitle, SegmentedControl, Select, Stack, Text, TextArea, TextInput, useAutoRefresh, type Column } from '@vello/web-theme';
import {
  maySelfApprove,
  AREA_WEEKDAYS,
  MAX_HOURS_ROWS,
  NEW_AREA_DRAFT,
  OPEN_ALL_DAY,
  RADIUS_LIMITS_METERS,
  addHoursRow,
  addRing,
  addVertex,
  areaDecideBlock,
  areaHistoryRows,
  clearDraft,
  closedProposals,
  deleteRing,
  deleteVertex,
  draftRadiusIssues,
  edgeMidpoint,
  formatRadiusKm,
  geometryRadiusMeters,
  insertVertex,
  moveVertex,
  parseLatLng,
  draftFindings,
  draftFromFences,
  draftFromProposal,
  draftGeometry,
  draftGeometryView,
  draftStats,
  formatAreaSize,
  formatChangePercent,
  formatHours,
  formatMarketInstant,
  geoJsonFileName,
  geoJsonText,
  hoursFindings,
  hoursFromFences,
  hoursFromSchedule,
  hoursToSchedule,
  isOvernight,
  tileOverride,
  marketTimeZone,
  mayEditArea,
  proposalStatusTone,
  radiusIssue,
  radiusMetersOf,
  removeHoursRow,
  replaceRings,
  selectRing,
  serviceAreaAuthority,
  serviceAreaPrimaryAction,
  setAreaFences,
  setHoursDefined,
  setHoursRow,
  setRadiusCenter,
  setRadiusMeters,
  setRadiusText,
  shrinkNeedsNote,
  shortOperatorId,
  switchAreaMode,
  toggleHoursDay,
  undoDraft,
  utcToZonedLocal,
  zonedLocalToUtc,
  type AreaDraft,
  type AreaHistoryRow,
  type AreaMode,
  type AreaHours,
  type AreaWeekday,
  type AreaRing,
} from '../../src/index';
import { MapCanvas, type MapArea, type MapDraftRing, type MapRadiusEdit } from '../components/map-canvas';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { usePersonNames } from '../components/person-name';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * Operações › Área de atendimento (D616 GEOFENCE-EDITOR, ADR-0121): the area a market declares it serves is drawn on the map —
 * one outline, or several separate ones — saved as the market's DRAFT, sent for approval, and published by ANOTHER manager
 * (the server refuses the maker's own approval; the screen never offers it). Approval publishes the next whole fence set; the
 * previous versions stay in the history, and a version can be shown on the map or downloaded.
 *
 * The drawing is local until saved: every tap, delete and undo happens in the browser, the same kernel (`@vello/geofence`)
 * that the server runs names what is wrong, and "Salvar rascunho" is the one primary action until the work is saved; then
 * "Enviar para aprovação"; and for the second manager "Aprovar e publicar". The tiles are configuration (Integrações › Tiles
 * do mapa): with none the drawing still works over the plain stage. Every colour is a token variable; every sentence — each
 * finding, each refusal, each status — is read from the central vocabulary. Neutral: no country, city or language is named.
 */

const METRIC_CTX = (locale: string, timeZone: string | undefined): FormatContext => ({ locale, timeZone: timeZone ?? 'UTC', measurement: 'metric' });

const detectFormat = (name: string | undefined, text: string): 'geojson' | 'kml' => (name !== undefined && /\.kml$/i.test(name)) || text.trimStart().startsWith('<') ? 'kml' : 'geojson';

/** Download a text as a file (an object URL on an anchor — no native dialog). */
function downloadText(text: string, fileName: string): void {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return;
  const url = URL.createObjectURL(new Blob([text], { type: 'application/geo+json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const pointsOf = (rings: readonly AreaRing[]): readonly { readonly latitude: number; readonly longitude: number }[] => rings.flat();

export function ServiceAreasScreen(): ReactElement {
  const { runtime, markets, grants, me } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { t, tp, lang } = useT();
  const { v, fail, refusal: refusalOf } = useVocab();
  const authority = serviceAreaAuthority(grants);

  const { market, choices } = useScopeMarket();
  const timeZone = marketTimeZone(market, markets);

  const [data, setData] = useState<ServiceAreasView | undefined>(undefined);
  const [config, setConfig] = useState<MapConfigView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<{ readonly tone: 'success' | 'danger'; readonly text: string } | undefined>(undefined);
  const [draft, setDraft] = useState<AreaDraft>(NEW_AREA_DRAFT);
  const [dirty, setDirty] = useState(false);
  const [reasonPick, setReasonPick] = useState<ServiceAreaPublicationReasonView | undefined>(undefined);
  const [note, setNote] = useState('');
  const [from, setFrom] = useState('');
  const [focus, setFocus] = useState<{ readonly key: string; readonly points: readonly { readonly latitude: number; readonly longitude: number }[] } | undefined>(undefined);
  const [previewVersion, setPreviewVersion] = useState<number | undefined>(undefined);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [hours, setHours] = useState<AreaHours>(OPEN_ALL_DAY);
  const [approveNote, setApproveNote] = useState('');
  const [noteDemanded, setNoteDemanded] = useState(false);
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);
  const [importText, setImportText] = useState('');
  const [importFiles, setImportFiles] = useState<readonly File[]>([]);
  const [moving, setMoving] = useState<{ readonly ring: number; readonly index: number } | undefined>(undefined);
  const [movingText, setMovingText] = useState('');
  const [moveIssue, setMoveIssue] = useState(false);
  const [importFormat, setImportFormat] = useState<'geojson' | 'kml'>('geojson');
  const [importIssue, setImportIssue] = useState<string | undefined>(undefined);
  const readSeq = useRef(0);
  const dirtyRef = useRef(false);
  const hydrated = useRef<string | undefined>(undefined);
  const focusSeq = useRef(0);

  const markDirty = useCallback((on: boolean): void => {
    dirtyRef.current = on;
    setDirty(on);
  }, []);

  const refuse = useCallback(
    (reasonCode: string | undefined, fallback: string, status?: number): string => {
      const text = refusalOf('refusal.admin.service_area', { code: fallback, reasonCode, status });
      return text ?? t('lmap.err.generic', { code: fail({ code: fallback, reasonCode }) });
    },
    [t, fail, refusalOf],
  );

  const focusOn = useCallback((rings: readonly AreaRing[]): void => {
    focusSeq.current += 1;
    setFocus({ key: `f${focusSeq.current}`, points: pointsOf(rings) });
  }, []);

  // The market's timezone can resolve AFTER the first render (the market list loads late): it is read lazily from a ref, so
  // `load` does not change with it and the reset below (which wipes the editor) runs only when the MARKET changes.
  const timeZoneRef = useRef(timeZone);
  timeZoneRef.current = timeZone;
  const toLocal = useCallback((utc: string): string => (timeZoneRef.current !== undefined ? utcToZonedLocal(utc, timeZoneRef.current) ?? '' : ''), []);

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    if (market === undefined) {
      setData(undefined);
      setError(undefined);
      return;
    }
    const res = await runtime.ops.serviceAreas(market);
    if (seq !== readSeq.current) return;
    if (!res.ok) {
      // A refused read keeps the area unknown: the screen says it could not load, never "no area declared".
      setError(refuse(res.error.reasonCode, res.error.code, res.error.status));
      return;
    }
    setError(undefined);
    setData(res.value);
    const open = res.value.open;
    const key = open === null ? 'none' : `${open.proposalId}:${open.updatedAt}`;
    // The saved proposal replaces the editor only when nothing unsaved is in it and the server's copy is new to this screen.
    if (!dirtyRef.current && hydrated.current !== key) {
      const first = hydrated.current === undefined;
      hydrated.current = key;
      if (open !== null) {
        const rings = draftFromProposal(open, lang);
        setDraft(rings);
        setHours(hoursFromSchedule(open.schedule));
        setReasonPick(open.reasonCode);
        setNote(open.note ?? '');
        setFrom(open.requestedFrom !== undefined ? toLocal(open.requestedFrom) : '');
        focusOn(rings.rings);
      } else if (!first) {
        // Someone else published or withdrew the proposal that was open: start clean.
        setDraft(NEW_AREA_DRAFT);
        setHours(OPEN_ALL_DAY);
        setNote('');
        setFrom('');
      }
    }
  }, [runtime, market, refuse, toLocal, focusOn, lang]);

  // `load` is read through a ref: its identity follows the language and the vocabulary (the refusal sentences), and a re-run
  // of this reset for those would drop an unsaved drawing. The editor resets for a different market only.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    setData(undefined);
    setError(undefined);
    setNotice(undefined);
    setDraft(NEW_AREA_DRAFT);
    setHours(OPEN_ALL_DAY);
    setApproveNote('');
    setNoteDemanded(false);
    setConfirmingWithdraw(false);
    markDirty(false);
    setReasonPick(undefined);
    setNote('');
    setFrom('');
    setFocus(undefined);
    setPreviewVersion(undefined);
    setRejecting(false);
    setImportIssue(undefined);
    hydrated.current = undefined;
    void loadRef.current();
  }, [runtime, market, markDirty]);

  useEffect(() => {
    setConfig(undefined);
    if (market === undefined) return;
    // The tiles are the Live map's slot (`ops:view`); an operator without it still gets the plain-stage drawing.
    void runtime.ops.mapConfig(market).then((res) => {
      setConfig(res.ok ? res.value : undefined);
    });
  }, [runtime, market]);

  const openId = data?.open?.proposalId;
  useEffect(() => {
    setApproveNote('');
    setNoteDemanded(false);
    setConfirmingWithdraw(false);
  }, [openId]);

  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== undefined });

  const open = data?.open ?? undefined;
  const mayEdit = mayEditArea(authority.mayManage, me?.id, open);
  const published = data?.published ?? undefined;
  const center = data?.center ?? config?.market.center;

  const findings = useMemo(() => draftFindings(draft, data?.center), [draft, data?.center]);
  const hoursIssues = useMemo(() => hoursFindings(hours), [hours]);
  const radiusIssues = useMemo(() => draftRadiusIssues(draft), [draft]);
  const radiusMode = draft.mode === 'radius';
  const stats = useMemo(() => draftStats(draft), [draft]);
  const geometry = useMemo(() => draftGeometry(draft), [draft]);
  const history = useMemo(() => areaHistoryRows(data?.history ?? []), [data?.history]);
  const earlier = useMemo(() => closedProposals(data?.proposals ?? []), [data?.proposals]);
  const personName = usePersonNames([
    open?.proposedBy,
    published?.publishedBy,
    published?.approvedBy,
    ...(data?.history ?? []).flatMap((r) => [r.publishedBy, r.approvedBy]),
    ...(data?.proposals ?? []).flatMap((x) => [x.proposedBy, x.decidedBy]),
  ]);
  /** An operator by name (their account's), the short id only while the name is unknown. */
  const person = (id: string): string => personName(id) ?? shortOperatorId(id);
  const reason: ServiceAreaPublicationReasonView = reasonPick ?? (published !== undefined && history.some((h) => h.hasArea) ? 'SERVICE_AREA_CORRECTION' : 'SERVICE_AREA_FIRST_DRAWING');
  const block = areaDecideBlock(open, me?.id, authority.mayManage, selfApproval);
  const primary = serviceAreaPrimaryAction({ mayManage: authority.mayManage, operatorId: me?.id, selfApproval, open, dirty, hasDrawing: geometry !== undefined && hoursIssues.length === 0, findingCount: findings.length + hoursIssues.length + radiusIssues.length });
  const hasVertices = draft.rings.some((r) => r.length > 0) || (radiusMode && draft.radius.center !== undefined);

  const preview: AreaHistoryRow | undefined = previewVersion !== undefined ? history.find((h) => h.version === previewVersion) : undefined;

  const mapAreas = useMemo<readonly MapArea[]>(() => {
    const inForce = (data?.inForce ?? []).flatMap((f, i) => draftFromFences([f]).rings.map((r, j) => ({ id: `force:${i}:${j}`, points: r, colorVar: 'var(--color-action-primary)', label: f.id })));
    const shown = preview?.rings.map((r, i) => ({ id: `preview:${i}`, points: r, colorVar: 'var(--color-warning)', label: `v${preview.version}` })) ?? [];
    return [...inForce, ...shown];
  }, [data?.inForce, preview]);
  const mapRings = useMemo<readonly MapDraftRing[]>(() => (radiusMode ? [] : draft.rings).map((r, i) => ({ id: `draft:${i}`, points: r, active: i === draft.active, ring: i })), [draft, radiusMode]);

  const radiusCenter = draft.radius.center;
  const radiusEdit: MapRadiusEdit | undefined =
    radiusMode && radiusCenter !== undefined
      ? {
          center: radiusCenter,
          radiusMeters: radiusMetersOf(draft.radius),
          handleLabel: t('sarea.radius.handle'),
          centerLabel: t('sarea.radius.centre'),
          ...(mayEdit ? { onCommit: (meters: number) => edit((d) => setRadiusMeters(d, meters, lang)) } : {}),
        }
      : undefined;
  const radiusProblem = radiusMode ? radiusIssue(draft.radius) : undefined;
  const radiusLimitText = { min: formatRadiusKm(RADIUS_LIMITS_METERS.min, lang), max: formatRadiusKm(RADIUS_LIMITS_METERS.max, lang) };
  const publishedRadius = history.find((h) => h.version === published?.version)?.radiusMeters;
  const openRadius = open !== undefined ? geometryRadiusMeters(open.geometry) : undefined;

  // ---- editing ------------------------------------------------------------------------------------------------------

  const edit = (next: (d: AreaDraft) => AreaDraft): void => {
    if (!mayEdit) return;
    setDraft(next);
    markDirty(true);
    setNotice(undefined);
  };

  const editHours = (next: AreaHours): void => {
    if (!mayEdit) return;
    setHours(next);
    markDirty(true);
    setNotice(undefined);
  };

  const startFromPublished = (): void => {
    const rings = draftFromFences(data?.inForce ?? [], lang).rings;
    edit((d) => replaceRings(d, rings, lang));
    editHours(hoursFromFences(data?.inForce ?? []));
    focusOn(rings);
  };

  /** A published version becomes the (unsaved) drawing and hours of the editor. */
  const restoreVersion = (row: AreaHistoryRow): void => {
    edit((d) => replaceRings(d, row.rings, lang));
    editHours(row.hours);
    focusOn(row.rings);
    setNotice({ tone: 'success', text: t('sarea.done.restored', { value: row.version }) });
  };

  /** "Raio" / "Desenho": one undoable edit; what is on the map is reused (the circle becomes the outline, the drawing becomes a circle). */
  const chooseMode = (mode: AreaMode): void => {
    if (!mayEdit || mode === draft.mode) return;
    const next = switchAreaMode(draft, mode, { marketCenter: center, locale: lang });
    edit(() => next);
    if (next.rings.length > 0) focusOn(next.rings);
  };

  const useCityCenter = (): void => {
    if (center === undefined) return;
    const next = setRadiusCenter(draft, center, lang);
    edit(() => next);
    focusOn(next.rings);
  };

  const readFile = async (files: readonly File[]): Promise<void> => {
    setImportFiles(files);
    setImportIssue(undefined);
    const file = files[0];
    if (file === undefined) return;
    try {
      const text = await file.text();
      setImportText(text);
      setImportFormat(detectFormat(file.name, text));
    } catch {
      setImportIssue(v('geofence.service_area_issue', 'IMPORT_UNREADABLE'));
    }
  };

  const applyImport = (): void => {
    const text = importText.trim();
    const result = importServiceArea({ format: importFormat, content: text });
    if (!result.ok) {
      setImportIssue(v('geofence.service_area_issue', result.issue));
      return;
    }
    const rings = serviceAreaRings(result.geometry);
    edit((d) => replaceRings(d, rings, lang));
    focusOn(rings);
    setImportIssue(undefined);
    setImportText('');
    setImportFiles([]);
    setNotice({ tone: 'success', text: t('sarea.import.done') });
  };

  const exportRings = (rings: readonly AreaRing[], version: number | 'draft'): void => {
    const text = geoJsonText(rings);
    if (text !== undefined && market !== undefined) downloadText(text, geoJsonFileName(market, version));
  };

  // ---- the server calls ---------------------------------------------------------------------------------------------

  const save = async (): Promise<void> => {
    const geo = draftGeometryView(draft);
    if (market === undefined || geo === undefined) return;
    let requestedFrom: string | null = null;
    if (from.trim() !== '') {
      const utc = timeZone !== undefined ? zonedLocalToUtc(from, timeZone) : zonedLocalToUtc(from, 'UTC');
      if (utc === undefined) {
        setNotice({ tone: 'danger', text: t('sarea.issue.from') });
        return;
      }
      requestedFrom = utc;
    }
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.saveServiceAreaDraft(market, { geometry: geo, requestedFrom, reasonCode: reason, schedule: hoursToSchedule(hours), ...(note.trim() !== '' ? { note: note.trim() } : {}) });
    setBusy(false);
    if (res.ok) {
      hydrated.current = `${res.value.proposal.proposalId}:${res.value.proposal.updatedAt}`;
      markDirty(false);
      setNotice({ tone: 'success', text: t('sarea.done.saved') });
    } else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const submit = async (): Promise<void> => {
    if (market === undefined || open === undefined) return;
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.submitServiceArea(market, open.proposalId);
    setBusy(false);
    setNotice(res.ok ? { tone: 'success', text: t('sarea.done.submitted') } : { tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const withdraw = async (): Promise<void> => {
    if (market === undefined || open === undefined) return;
    setBusy(true);
    setNotice(undefined);
    const wasMine = open.proposedBy === me?.id;
    const res = await runtime.ops.withdrawServiceArea(market, open.proposalId);
    setBusy(false);
    setConfirmingWithdraw(false);
    if (res.ok) {
      setRejecting(false);
      // Withdrawing keeps the drawing in the editor (marked unsaved) so the author can adjust it and save a new draft.
      if (wasMine) markDirty(true);
      setNotice({ tone: 'success', text: t('sarea.done.withdrawn') });
    } else setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    await load();
  };

  const decide = async (decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    if (market === undefined || open === undefined) return;
    setBusy(true);
    setNotice(undefined);
    const shrinkNote = approveNote.trim();
    const res = await runtime.ops.decideServiceArea(market, open.proposalId, { decision, ...(decision === 'REJECT' ? { reason: rejectReason.trim() } : shrinkNote !== '' ? { reason: shrinkNote } : {}) });
    setBusy(false);
    if (res.ok) {
      setNotice({ tone: 'success', text: decision === 'APPROVE' ? t('sarea.done.approved', { value: res.value.published?.version ?? res.value.proposal.publishedVersion ?? '' }) : t('sarea.done.rejected') });
      setRejecting(false);
      setRejectReason('');
      setApproveNote('');
      setNoteDemanded(false);
      markDirty(false);
    } else {
      // The server wants the shrink note: show the field even if the impact read did not say so.
      if (res.error.reasonCode === 'SHRINK_NOTE_REQUIRED') setNoteDemanded(true);
      setNotice({ tone: 'danger', text: refuse(res.error.reasonCode, res.error.code, res.error.status) });
    }
    await load();
  };

  // ---- the lists ----------------------------------------------------------------------------------------------------

  const dayName = (d: AreaWeekday): string => t(`sarea.day.${d}`);
  /** The hours as one line: "Mon-Fri 06:00-23:00; Sat 08:00-12:00", or "Open 24 hours". */
  const hoursText = (h: AreaHours): string => {
    const lines = h.defined ? formatHours(hoursToSchedule(h), dayName) : [];
    return h.defined && lines.length > 0 ? lines.join('; ') : t('sarea.hours.always');
  };
  const nowVersion = published?.version;
  const historyColumns: readonly Column<AreaHistoryRow>[] = [
    {
      key: 'version',
      header: t('sarea.history.col.version'),
      width: 'minmax(140px, .9fr)',
      render: (r) => (
        <Row gap={2} wrap align="center">
          <Text as="strong" className="tnum">{t('sarea.status.published', { value: r.version })}</Text>
          {r.version === nowVersion && <Badge tone="success">{t('sarea.history.current')}</Badge>}
        </Row>
      ),
    },
    { key: 'when', header: t('sarea.history.col.when'), width: 'minmax(170px, 1fr)', render: (r) => <Text role="caption" tone="secondary" className="tnum">{formatMarketInstant(r.publishedAt, timeZone, lang)}</Text> },
    { key: 'reason', header: t('sarea.history.col.reason'), width: 'minmax(170px, 1.1fr)', render: (r) => <Text role="caption">{v('geofence.set_reason', r.reasonCode)}</Text> },
    {
      key: 'trail',
      header: t('sarea.history.col.trail'),
      width: 'minmax(200px, 1.2fr)',
      render: (r) => <Text role="caption" tone="secondary">{t('sarea.status.trail', { proposer: person(r.publishedBy), approver: person(r.approvedBy) })}</Text>,
    },
    {
      key: 'area',
      header: t('sarea.history.col.area'),
      width: 'minmax(130px, .8fr)',
      render: (r) => <Text role="caption" tone="secondary" className="tnum">{r.radiusMeters !== undefined ? t('sarea.radius.read', { value: formatRadiusKm(r.radiusMeters, lang) }) : r.hasArea ? tp('sarea.history.rings', r.rings.length) : t('sarea.history.noArea')}</Text>,
    },
    { key: 'hours', header: t('sarea.history.col.hours'), width: 'minmax(170px, 1fr)', render: (r) => <Text role="caption" tone="secondary" className="tnum">{hoursText(r.hours)}</Text> },
    {
      key: 'act',
      header: '',
      width: 'minmax(420px, 2fr)',
      render: (r) => (
        <Row gap={2} wrap align="center">
          {r.hasArea && (
            <Button variant="ghost" aria-label={t('sarea.history.previewAria', { value: r.version })} aria-pressed={previewVersion === r.version} onClick={() => { setPreviewVersion(previewVersion === r.version ? undefined : r.version); }}>
              {t('sarea.history.preview')}
            </Button>
          )}
          {r.hasArea && (
            <Button variant="ghost" aria-label={t('sarea.history.exportAria', { value: r.version })} onClick={() => exportRings(r.rings, r.version)}>
              {t('sarea.history.export')}
            </Button>
          )}
          {r.hasArea && mayEdit && (
            <Button variant="ghost" aria-label={t('sarea.history.restoreAria', { value: r.version })} onClick={() => restoreVersion(r)}>
              {t('sarea.history.restore')}
            </Button>
          )}
        </Row>
      ),
    },
  ];

  const proposalColumns: readonly Column<(typeof earlier)[number]>[] = [
    { key: 'status', header: t('sarea.proposals.col.status'), width: 'minmax(180px, 1fr)', render: (p) => <Badge tone={proposalStatusTone(p.status)}>{v('geofence.service_area_status', p.status)}</Badge> },
    { key: 'reason', header: t('sarea.proposals.col.reason'), width: 'minmax(190px, 1.2fr)', render: (p) => <Text role="caption">{v('geofence.service_area_reason', p.reasonCode)}</Text> },
    {
      key: 'trail',
      header: t('sarea.proposals.col.trail'),
      width: 'minmax(260px, 1.8fr)',
      render: (p) => (
        <Text role="caption" tone="secondary">
          {t('sarea.proposals.proposed', { value: person(p.proposedBy) })}
          {p.decidedBy !== undefined ? ` · ${t('sarea.proposals.decided', { value: person(p.decidedBy) })}` : ''}
          {p.decisionReason !== undefined ? ` — ${p.decisionReason}` : ''}
        </Text>
      ),
    },
    { key: 'when', header: t('sarea.history.col.when'), width: 'minmax(170px, 1fr)', render: (p) => <Text role="caption" tone="secondary" className="tnum">{formatMarketInstant(p.updatedAt, timeZone, lang)}</Text> },
  ];

  const activeRing = draft.rings[draft.active];
  const ringLabel = (i: number): string => t('sarea.ring.name', { value: i + 1 });
  const ctx = METRIC_CTX(lang, timeZone);
  const needsApproveNote = shrinkNeedsNote(open?.impact) || noteDemanded;
  const canSubmit = open !== undefined && open.status === 'DRAFT' && open.proposedBy === me?.id;

  return (
    <Stack gap={5}>
      <PageHeader title={t('sarea.title')} subtitle={t('sarea.subtitle')} />

      {!authority.mayManage && <Notice tone="neutral">{t('sarea.readOnly')}</Notice>}
      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}

      {market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : data === undefined ? (
        <Panel>
          <Text role="caption" tone="secondary" as="p">{error !== undefined ? t('sarea.empty.failed') : t('ui.loading')}</Text>
        </Panel>
      ) : (
        <>
          <Panel>
            <Stack gap={3}>
              <SectionTitle>{t('sarea.status.title')}</SectionTitle>
              {published === undefined ? (
                <Text as="p">{t('sarea.status.none')}</Text>
              ) : (
                <Stack gap={1}>
                  <Row gap={2} wrap align="center">
                    <Text as="strong" className="tnum">{t('sarea.status.published', { value: published.version })}</Text>
                    <Badge tone={history.find((h) => h.version === published.version)?.hasArea === true ? 'success' : 'warning'}>{t('sarea.status.since', { value: formatMarketInstant(published.publishedAt, timeZone, lang) })}</Badge>
                  </Row>
                  {history.find((h) => h.version === published.version)?.hasArea === false && <Text as="p" tone="secondary">{t('sarea.status.noArea', { value: published.version })}</Text>}
                  {publishedRadius !== undefined && <Text as="p" className="tnum">{t('sarea.radius.read', { value: formatRadiusKm(publishedRadius, lang) })}</Text>}
                  <Text role="caption" tone="secondary" as="p">{t('sarea.status.reason', { value: v('geofence.set_reason', published.reasonCode) })}</Text>
                  <Text role="caption" tone="secondary" as="p" className="tnum">{t('sarea.hours.read', { value: hoursText(hoursFromFences(setAreaFences(published))), zone: timeZone ?? 'UTC' })}</Text>
                  <Text role="caption" tone="secondary" as="p">{t('sarea.status.trail', { proposer: person(published.publishedBy), approver: person(published.approvedBy) })}</Text>
                </Stack>
              )}

              <SectionTitle>{t('sarea.open.title')}</SectionTitle>
              {open === undefined ? (
                <Text role="caption" tone="secondary" as="p">{t('sarea.open.none')}</Text>
              ) : (
                <Stack gap={2}>
                  <Row gap={2} wrap align="center">
                    <Badge tone={proposalStatusTone(open.status)}>{v('geofence.service_area_status', open.status)}</Badge>
                    <Text role="caption" tone="secondary" className="tnum">{t('sarea.open.by', { proposer: person(open.proposedBy), value: formatMarketInstant(open.createdAt, timeZone, lang) })}</Text>
                  </Row>
                  {openRadius !== undefined && <Text as="p" className="tnum">{t('sarea.radius.read', { value: formatRadiusKm(openRadius, lang) })}</Text>}
                  <Text role="caption" tone="secondary" as="p">{v('geofence.service_area_reason', open.reasonCode)}{open.note !== undefined ? ` — ${open.note}` : ''}</Text>
                  <Text role="caption" tone="secondary" as="p" className="tnum">{t('sarea.hours.read', { value: hoursText(hoursFromSchedule(open.schedule)), zone: timeZone ?? 'UTC' })}</Text>
                  {open.requestedFrom !== undefined && <Text role="caption" tone="secondary" as="p" className="tnum">{t('sarea.open.requestedFrom', { value: formatMarketInstant(open.requestedFrom, timeZone, lang) })}</Text>}
                  {open.status === 'DRAFT' && open.proposedBy !== me?.id && authority.mayManage && <Notice tone="neutral">{t('sarea.open.byOther')}</Notice>}
                  {open.status === 'SUBMITTED' && block === 'OWN_PROPOSAL' && <Notice tone="neutral">{t('sarea.open.waitOther')}</Notice>}
                  {open.status === 'SUBMITTED' && block === undefined && <Notice tone="neutral">{t('sarea.open.review')}</Notice>}
                </Stack>
              )}
            </Stack>
          </Panel>

          <Panel>
            <Stack gap={3}>
              <SectionTitle>{t('sarea.map.title')}</SectionTitle>
              {mayEdit && (
                <SegmentedControl<AreaMode>
                  ariaLabel={t('sarea.mode.label')}
                  value={draft.mode}
                  onChange={chooseMode}
                  options={[{ id: 'radius', label: t('sarea.mode.radius') }, { id: 'draw', label: t('sarea.mode.draw') }]}
                />
              )}
              <Text as="p" role="caption" tone="secondary">{mayEdit ? (radiusMode ? t('sarea.radius.hint') : t('sarea.map.hint')) : t('sarea.map.readOnly')}</Text>
              {open !== undefined && open.status === 'SUBMITTED' && authority.mayManage && <Text as="p" role="caption" tone="secondary">{t('sarea.open.locked')}</Text>}
              <MapCanvas
                key={market}
                tiles={tileOverride(config?.tiles)}
                center={center}
                points={[]}
                areas={mapAreas}
                draftRings={mapRings}
                {...(focus !== undefined ? { focus } : {})}
                {...(radiusEdit !== undefined ? { radius: radiusEdit } : {})}
                {...(mayEdit && radiusMode
                  ? { onMapTap: (p: { latitude: number; longitude: number }) => edit((d) => setRadiusCenter(d, p, lang)) }
                  : mayEdit
                    ? {
                        onMapTap: (p: { latitude: number; longitude: number }) => edit((d) => addVertex(d, p)),
                        onVertexMove: (ring: number, index: number, p: { latitude: number; longitude: number }) => edit((d) => moveVertex(d, ring, index, p)),
                        onEdgeInsert: (ring: number, atIndex: number, p: { latitude: number; longitude: number }) => edit((d) => insertVertex(d, ring, atIndex, p)),
                      }
                    : {})}
                ariaLabel={t('sarea.map.label')}
                pointLabel={() => ''}
              />
              {mayEdit && radiusMode && (
                <Measure size="md" gap={3}>
                  <Field label={t('sarea.radius.field')} htmlFor="sarea-radius" hint={t('sarea.radius.fieldHint', radiusLimitText)}>
                    <TextInput id="sarea-radius" className="tnum" inputMode="decimal" value={draft.radius.radiusKm} onChange={(e) => edit((d) => setRadiusText(d, e.target.value))} />
                  </Field>
                  {radiusProblem !== undefined && <Notice tone={radiusProblem === 'CENTER_REQUIRED' || radiusProblem === 'RADIUS_REQUIRED' ? 'neutral' : 'danger'}>{t(`sarea.radius.issue.${radiusProblem}`, radiusLimitText)}</Notice>}
                  {center !== undefined && (
                    <Row gap={2}>
                      <Button variant="secondary" onClick={useCityCenter}>{t('sarea.radius.cityCenter')}</Button>
                    </Row>
                  )}
                </Measure>
              )}
              {preview !== undefined && (
                <Row gap={3} wrap align="center">
                  <Text role="caption" tone="secondary">{t('sarea.map.previewing', { value: preview.version })}</Text>
                  <Button variant="ghost" onClick={() => setPreviewVersion(undefined)}>{t('sarea.map.stopPreview')}</Button>
                </Row>
              )}
              <Row gap={2} wrap>
                {mayEdit && <Button variant="secondary" disabled={draft.past.length === 0} onClick={() => edit(undoDraft)}>{t('sarea.action.undo')}</Button>}
                {mayEdit && <Button variant="secondary" disabled={(data.inForce ?? []).length === 0} onClick={startFromPublished}>{t('sarea.action.startFromPublished')}</Button>}
                {mayEdit && <Button variant="ghost" disabled={!hasVertices} onClick={() => edit(clearDraft)}>{t('sarea.action.clear')}</Button>}
                <Button variant="ghost" disabled={geometry === undefined} onClick={() => exportRings(draft.rings, 'draft')}>{t('sarea.action.exportDraft')}</Button>
              </Row>
            </Stack>
          </Panel>

          {!radiusMode && (
          <Panel>
            <Stack gap={3}>
              <SectionTitle>{t('sarea.rings.title')}</SectionTitle>
              <Text as="p" role="caption" tone="secondary">{t('sarea.rings.hint')}</Text>
              {draft.rings.length === 0 ? (
                <Text as="p" tone="secondary">{t('sarea.rings.none')}</Text>
              ) : (
                <Stack gap={2}>
                  {draft.rings.map((r, i) => (
                    <Row key={`ring:${i}`} gap={2} wrap align="center">
                      <Button variant={i === draft.active ? 'secondary' : 'ghost'} aria-pressed={i === draft.active} onClick={() => setDraft((d) => selectRing(d, i))}>
                        {ringLabel(i)} · {tp('sarea.ring.points', r.length)}
                      </Button>
                      {i === draft.active && <Badge tone="neutral">{t('sarea.ring.active')}</Badge>}
                      {mayEdit && <Button variant="ghost" aria-label={t('sarea.action.deleteRingAria', { value: i + 1 })} onClick={() => edit((d) => deleteRing(d, i))}>{t('sarea.action.deleteRing')}</Button>}
                    </Row>
                  ))}
                </Stack>
              )}
              {mayEdit && (
                <Row gap={2}>
                  <Button variant="secondary" disabled={draft.rings.length >= SERVICE_AREA_LIMITS.maxRings || (activeRing !== undefined && activeRing.length === 0)} onClick={() => edit(addRing)}>{t('sarea.action.addRing')}</Button>
                </Row>
              )}
              {activeRing !== undefined && (
                activeRing.length === 0 ? (
                  <Text as="p" role="caption" tone="secondary">{t('sarea.vertex.none')}</Text>
                ) : (
                  <Stack gap={1}>
                    {mayEdit && moving !== undefined && moving.ring === draft.active && activeRing[moving.index] !== undefined && (
                      <Measure size="md" gap={2}>
                        <Field label={t('sarea.vertex.moveLabel', { value: moving.index + 1 })} htmlFor="sarea-move" hint={t('sarea.vertex.moveHint')}>
                          <TextInput id="sarea-move" className="tnum" value={movingText} placeholder="-0.000000, 0.000000" onChange={(e) => { setMovingText(e.target.value); setMoveIssue(false); }} />
                        </Field>
                        {moveIssue && <Notice tone="danger">{t('sarea.vertex.moveInvalid')}</Notice>}
                        <Row gap={2}>
                          <Button
                            variant="secondary"
                            onClick={() => {
                              const point = parseLatLng(movingText);
                              if (point === undefined) {
                                setMoveIssue(true);
                                return;
                              }
                              edit((d) => moveVertex(d, moving.ring, moving.index, point));
                              setMoving(undefined);
                            }}
                          >
                            {t('sarea.vertex.moveApply')}
                          </Button>
                          <Button variant="ghost" onClick={() => setMoving(undefined)}>{t('ui.cancel')}</Button>
                        </Row>
                      </Measure>
                    )}
                    {activeRing.map((p, i) => (
                      <Row key={`v:${draft.active}:${i}`} gap={3} wrap align="center" justify="between">
                        <Text className="tnum">{t('sarea.vertex.name', { value: i + 1 })} — {p.latitude}, {p.longitude}</Text>
                        {mayEdit && (
                          <Button variant="ghost" aria-label={t('sarea.action.moveVertexAria', { value: i + 1 })} onClick={() => { setMoving({ ring: draft.active, index: i }); setMovingText(`${p.latitude}, ${p.longitude}`); setMoveIssue(false); }}>
                            {t('sarea.action.moveVertex')}
                          </Button>
                        )}
                        {mayEdit && edgeMidpoint(activeRing, i) !== undefined && (
                          <Button
                            variant="ghost"
                            aria-label={t('sarea.action.insertAfterAria', { value: i + 1 })}
                            onClick={() => {
                              const mid = edgeMidpoint(activeRing, i);
                              if (mid !== undefined) edit((d) => insertVertex(d, d.active, i + 1, mid));
                            }}
                          >
                            {t('sarea.action.insertAfter')}
                          </Button>
                        )}
                        {mayEdit && <Button variant="ghost" aria-label={t('sarea.action.deleteVertexAria', { value: i + 1 })} onClick={() => edit((d) => deleteVertex(d, d.active, i))}>{t('sarea.action.deleteVertex')}</Button>}
                      </Row>
                    ))}
                  </Stack>
                )
              )}
            </Stack>
          </Panel>
          )}

          <Panel>
            <Stack gap={3}>
              <SectionTitle>{t('sarea.findings.title')}</SectionTitle>
              {findings.length === 0 ? (
                <Notice tone="success">{t('sarea.findings.ok')}</Notice>
              ) : (
                <Stack gap={2}>
                  {findings.map((f, i) => {
                    const text = v('geofence.service_area_issue', f.issue);
                    return (
                      <Notice key={`${f.issue}:${f.ring ?? 'all'}:${i}`} tone={f.issue === 'SERVICE_AREA_EMPTY' ? 'neutral' : 'danger'}>
                        {f.ring !== undefined && draft.rings.length > 1 ? t('sarea.findings.ring', { text, ring: ringLabel(f.ring) }) : text}
                      </Notice>
                    );
                  })}
                </Stack>
              )}
              <SectionTitle>{t('sarea.stats.title')}</SectionTitle>
              {stats === undefined ? (
                <Text role="caption" tone="secondary" as="p">{t('sarea.stats.none')}</Text>
              ) : (
                <Row gap={5} wrap>
                  <Stack gap={1}><Text role="caption" tone="secondary">{t('sarea.stats.area')}</Text><Text as="strong" className="tnum">{formatAreaSize(stats.areaSquareMeters, ctx.measurement, lang)}</Text></Stack>
                  <Stack gap={1}><Text role="caption" tone="secondary">{t('sarea.stats.perimeter')}</Text><Text as="strong" className="tnum">{formatDistance(stats.perimeterMeters, ctx)}</Text></Stack>
                  {radiusMode && radiusMetersOf(draft.radius) !== undefined && <Stack gap={1}><Text role="caption" tone="secondary">{t('sarea.radius.field')}</Text><Text as="strong" className="tnum">{formatRadiusKm(radiusMetersOf(draft.radius) ?? 0, lang)}</Text></Stack>}
                  {!radiusMode && <Stack gap={1}><Text role="caption" tone="secondary">{t('sarea.stats.rings')}</Text><Text as="strong" className="tnum">{stats.rings}</Text></Stack>}
                  {!radiusMode && <Stack gap={1}><Text role="caption" tone="secondary">{t('sarea.stats.vertices')}</Text><Text as="strong" className="tnum">{stats.vertices}</Text></Stack>}
                </Row>
              )}
            </Stack>
          </Panel>

          {mayEdit && (
            <Panel>
              <Stack gap={3}>
                <SectionTitle>{t('sarea.hours.title')}</SectionTitle>
                <Text as="p" role="caption" tone="secondary">{t('sarea.hours.hint')}</Text>
                <Text as="p" role="caption" tone="secondary" className="tnum">{t('sarea.hours.zone', { value: timeZone ?? 'UTC' })}</Text>
                <SegmentedControl<'always' | 'defined'>
                  ariaLabel={t('sarea.hours.mode')}
                  value={hours.defined ? 'defined' : 'always'}
                  onChange={(m) => editHours(setHoursDefined(hours, m === 'defined'))}
                  options={[{ id: 'always', label: t('sarea.hours.always') }, { id: 'defined', label: t('sarea.hours.defined') }]}
                />
                {hours.defined && (
                  <Stack gap={4}>
                    {hours.rows.map((row, i) => (
                      <Stack key={`hrow:${i}`} gap={2}>
                        <Text as="strong">{t('sarea.hours.window', { value: i + 1 })}</Text>
                        <Row gap={3} wrap align="center" role="group" aria-label={t('sarea.hours.days', { value: i + 1 })}>
                          {AREA_WEEKDAYS.map((d) => (
                            <CheckRow key={d} id={`sarea-h-${i}-${d}`} checked={row.days.includes(d)} onChange={(on) => editHours(toggleHoursDay(hours, i, d, on))}>
                              {dayName(d)}
                            </CheckRow>
                          ))}
                        </Row>
                        <Row gap={3} wrap align="end">
                          <Field label={t('sarea.hours.start')} htmlFor={`sarea-h-${i}-start`}>
                            <TextInput id={`sarea-h-${i}-start`} className="tnum" type="time" value={row.start} onChange={(e) => editHours(setHoursRow(hours, i, { start: e.target.value }))} />
                          </Field>
                          <Field label={t('sarea.hours.end')} htmlFor={`sarea-h-${i}-end`}>
                            <TextInput id={`sarea-h-${i}-end`} className="tnum" type="time" value={row.end} onChange={(e) => editHours(setHoursRow(hours, i, { end: e.target.value }))} />
                          </Field>
                          <Button variant="ghost" aria-label={t('sarea.hours.removeAria', { value: i + 1 })} onClick={() => editHours(removeHoursRow(hours, i))}>{t('sarea.hours.remove')}</Button>
                        </Row>
                        {isOvernight(row) && <Text as="p" role="caption" tone="secondary">{t('sarea.hours.overnight')}</Text>}
                      </Stack>
                    ))}
                    <Row gap={2}>
                      <Button variant="secondary" disabled={hours.rows.length >= MAX_HOURS_ROWS} onClick={() => editHours(addHoursRow(hours))}>{t('sarea.hours.add')}</Button>
                    </Row>
                    <Text as="p" role="caption" tone="secondary">{t('sarea.hours.endHint')}</Text>
                  </Stack>
                )}
                {hoursIssues.map((f, i) => (
                  <Notice key={`${f.issue}:${i}`} tone="danger">{v('geofence.service_area_issue', f.issue)}</Notice>
                ))}
              </Stack>
            </Panel>
          )}

          {mayEdit && (
            <Panel>
              <Stack gap={3}>
                <SectionTitle>{t('sarea.import.title')}</SectionTitle>
                <Text as="p" role="caption" tone="secondary">{t('sarea.import.hint')}</Text>
                <Measure size="lg" gap={3}>
                  <Field label={t('sarea.import.file')} htmlFor="sarea-import-file">
                    <FileInput
                      id="sarea-import-file"
                      accept=".geojson,.json,.kml,application/geo+json,application/json,application/vnd.google-earth.kml+xml"
                      value={importFiles}
                      onChange={(files) => void readFile(files)}
                      chooseLabel={t('sarea.import.choose')}
                      replaceLabel={t('sarea.import.replace')}
                      emptyLabel={t('sarea.import.none')}
                      clearLabel={t('sarea.import.clear')}
                    />
                  </Field>
                  <Field label={t('sarea.import.text')} htmlFor="sarea-import-text">
                    <TextArea id="sarea-import-text" value={importText} onChange={(e) => { setImportText(e.target.value); setImportFormat(detectFormat(undefined, e.target.value)); setImportIssue(undefined); }} />
                  </Field>
                  <SegmentedControl<'geojson' | 'kml'>
                    ariaLabel={t('sarea.import.format')}
                    value={importFormat}
                    onChange={setImportFormat}
                    options={[{ id: 'geojson', label: t('sarea.import.format.geojson') }, { id: 'kml', label: t('sarea.import.format.kml') }]}
                  />
                  {importIssue !== undefined && <Notice tone="danger">{importIssue}</Notice>}
                  <Row gap={3}>
                    <Button variant="secondary" disabled={busy || importText.trim() === ''} onClick={applyImport}>{t('sarea.import.action')}</Button>
                  </Row>
                </Measure>
              </Stack>
            </Panel>
          )}

          {authority.mayManage && (
            <Panel>
              <Stack gap={3}>
                <SectionTitle>{t('sarea.form.title')}</SectionTitle>
                <Text as="p" role="caption" tone="secondary">{t('sarea.form.fourEyes')}</Text>
                {mayEdit && (
                  <Measure size="lg" gap={3}>
                    <Field label={t('sarea.form.reason')} htmlFor="sarea-reason" hint={t('sarea.form.reasonHint')}>
                      <Select id="sarea-reason" value={reason} onChange={(e) => { setReasonPick(e.target.value as ServiceAreaPublicationReasonView); markDirty(true); }}>
                        {SERVICE_AREA_PUBLICATION_REASONS.map((code) => (
                          <option key={code} value={code}>{v('geofence.service_area_reason', code)}</option>
                        ))}
                      </Select>
                    </Field>
                    <Field label={t('sarea.form.note')} htmlFor="sarea-note" hint={t('sarea.form.noteHint')}>
                      <TextArea id="sarea-note" value={note} maxLength={500} onChange={(e) => { setNote(e.target.value); markDirty(true); }} />
                    </Field>
                    <Field label={t('sarea.form.from')} htmlFor="sarea-from" hint={t('sarea.form.fromHint', { value: timeZone ?? 'UTC' })}>
                      <TextInput id="sarea-from" className="tnum" type="datetime-local" value={from} onChange={(e) => { setFrom(e.target.value); markDirty(true); }} />
                    </Field>
                  </Measure>
                )}

                {rejecting && open !== undefined && (
                  <Measure size="md" gap={3}>
                    <SectionTitle>{t('sarea.reject.title')}</SectionTitle>
                    <Field label={t('sarea.reject.reason')} htmlFor="sarea-reject" hint={t('sarea.reject.hint')}>
                      <TextArea id="sarea-reject" value={rejectReason} maxLength={500} onChange={(e) => setRejectReason(e.target.value)} />
                    </Field>
                    <Row gap={3}>
                      <Button variant="secondary" disabled={busy || rejectReason.trim() === ''} onClick={() => void decide('REJECT')}>{t('sarea.action.reject')}</Button>
                      <Button variant="ghost" disabled={busy} onClick={() => setRejecting(false)}>{t('ui.cancel')}</Button>
                    </Row>
                  </Measure>
                )}

                {open !== undefined && open.status === 'SUBMITTED' && block === undefined && (
                  <Measure size="lg" gap={3}>
                    <SectionTitle>{t('sarea.approve.title')}</SectionTitle>
                    {open.impact === null || open.impact === undefined ? (
                      <Text as="p" role="caption" tone="secondary">{t('sarea.approve.noImpact')}</Text>
                    ) : (
                      <Row gap={5} wrap>
                        <Stack gap={1}>
                          <Text role="caption" tone="secondary">{t('sarea.approve.change')}</Text>
                          <Text as="strong" className="tnum">{open.impact.changePercent === null ? t('sarea.approve.first') : formatChangePercent(open.impact.changePercent, lang)}</Text>
                        </Stack>
                        <Stack gap={1}>
                          <Text role="caption" tone="secondary">{t('sarea.approve.current')}</Text>
                          <Text as="strong" className="tnum">{open.impact.currentAreaSquareMeters === null ? t('sarea.approve.none') : formatAreaSize(open.impact.currentAreaSquareMeters, ctx.measurement, lang)}</Text>
                        </Stack>
                        <Stack gap={1}>
                          <Text role="caption" tone="secondary">{t('sarea.approve.new')}</Text>
                          <Text as="strong" className="tnum">{formatAreaSize(open.impact.newAreaSquareMeters, ctx.measurement, lang)}</Text>
                        </Stack>
                        <Stack gap={1}>
                          <Text role="caption" tone="secondary">{t('sarea.approve.drivers')}</Text>
                          <Text as="strong" className="tnum">{open.impact.driversOnlineOutside === null ? t('sarea.approve.unavailable') : open.impact.driversOnlineOutside}</Text>
                        </Stack>
                        <Stack gap={1}>
                          <Text role="caption" tone="secondary">{t('sarea.approve.rides')}</Text>
                          <Text as="strong" className="tnum">{open.impact.openRidesOutside === null ? t('sarea.approve.unavailable') : open.impact.openRidesOutside}</Text>
                        </Stack>
                      </Row>
                    )}
                    {needsApproveNote && (
                      <Field
                        label={t('sarea.approve.noteLabel')}
                        htmlFor="sarea-approve-note"
                        hint={open.impact?.shrinkConfirmPercent !== null && open.impact?.shrinkConfirmPercent !== undefined ? t('sarea.approve.noteHint', { value: open.impact.shrinkConfirmPercent }) : t('sarea.approve.noteHintAny')}
                      >
                        <TextArea id="sarea-approve-note" value={approveNote} maxLength={500} onChange={(e) => setApproveNote(e.target.value)} />
                      </Field>
                    )}
                  </Measure>
                )}

                {confirmingWithdraw && open !== undefined && (
                  <Measure size="lg" gap={3}>
                    <Notice tone="warning">{t('sarea.withdraw.confirm', { value: person(open.proposedBy) })}</Notice>
                    <Row gap={3}>
                      <Button variant="secondary" disabled={busy} onClick={() => void withdraw()}>{t('sarea.withdraw.confirmAction')}</Button>
                      <Button variant="ghost" disabled={busy} onClick={() => setConfirmingWithdraw(false)}>{t('ui.cancel')}</Button>
                    </Row>
                  </Measure>
                )}

                {dirty && mayEdit && <Text role="caption" tone="secondary" as="p">{t('sarea.form.unsaved')}</Text>}
                {canSubmit && !dirty && findings.length + hoursIssues.length + radiusIssues.length > 0 && <Text role="caption" tone="secondary" as="p">{t('sarea.form.fixFirst')}</Text>}
                {canSubmit && dirty && <Text role="caption" tone="secondary" as="p">{t('sarea.form.saveFirst')}</Text>}

                <Row gap={3} wrap>
                  {mayEdit && geometry !== undefined && (
                    <Button variant={primary === 'SAVE' ? 'primary' : 'secondary'} disabled={busy || hoursIssues.length > 0 || (!dirty && open !== undefined)} onClick={() => void save()}>{t('sarea.action.save')}</Button>
                  )}
                  {canSubmit && (
                    <Button variant={primary === 'SUBMIT' ? 'primary' : 'secondary'} disabled={busy || dirty || findings.length > 0 || hoursIssues.length > 0 || radiusIssues.length > 0} onClick={() => void submit()}>{t('sarea.action.submit')}</Button>
                  )}
                  {open !== undefined && open.status === 'SUBMITTED' && block === undefined && (
                    <>
                      <Button variant={primary === 'APPROVE' ? 'primary' : 'secondary'} disabled={busy || (needsApproveNote && approveNote.trim() === '')} onClick={() => void decide('APPROVE')}>{t('sarea.action.approve')}</Button>
                      {/* D868: a super administrator approving its own proposal withdraws it instead of rejecting it. */}
                      {open.proposedBy !== me?.id && <Button variant="ghost" disabled={busy} onClick={() => { setRejecting(true); setRejectReason(''); setNotice(undefined); }}>{t('sarea.action.reject')}</Button>}
                    </>
                  )}
                  {open !== undefined && (
                    <Button variant="ghost" disabled={busy} onClick={() => (open.proposedBy !== me?.id ? setConfirmingWithdraw(true) : void withdraw())}>{t('sarea.action.withdraw')}</Button>
                  )}
                </Row>
              </Stack>
            </Panel>
          )}

          <Stack gap={3}>
            <SectionTitle>{t('sarea.history.title')}</SectionTitle>
            <ListTable
              columns={historyColumns}
              rows={history}
              rowKey={(r) => `v${r.version}`}
              loading={false}
              empty={<EmptyState title={t('sarea.history.empty')} />}
              minWidth={1300}
            />
          </Stack>

          {earlier.length > 0 && (
            <Stack gap={3}>
              <SectionTitle>{t('sarea.proposals.title')}</SectionTitle>
              <ListTable columns={proposalColumns} rows={earlier} rowKey={(p) => p.proposalId} loading={false} minWidth={900} />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}
